/**
 * Owns the asynchronous Step06 revision execution lifecycle. It is injected
 * with page-bound rendering and state helpers so the transport/result handling
 * stays independently testable and does not change the Step03 writeback contract.
 */
export function createRevisionApplyController({
  select,
  getElementById,
  getState,
  setState,
  getPendingCandidate,
  clearPendingCandidate,
  restoreCandidate,
  pushCandidate,
  getCandidateVersionCount,
  collectMasterInstructions,
  collectSupplementalFacts,
  getRevisionMode,
  getRevisionBaselineQuality,
  getCurrentDraftRevisionKey,
  canApplyRevision,
  collectCurrentListing,
  resolveSnapshotAsinsForApi,
  normalizeRevisionAdviceText,
  buildEffectiveRevisionAdviceText,
  buildRevisionPayload,
  compactRevisionGenerationLedger = (ledger) => ledger,
  buildListingRevisionTextHash,
  renderListingRevisionText,
  revisionProtocol,
  diagnosticFacade,
  appendClientTraceCalls,
  formatRevisionUserFacingReason = (value) => String(value || ''),
  renderExecutionStatus,
  showToast,
  getTextRevisionWritebackDecision,
  normalizeRevisionSupplementalFacts,
  renderFactPanel,
  deriveRevisionChangedFields,
  getInvalidRevisionChangedFields,
  mergeRevisionDraftByChangedFields,
  buildRevisionDiffItems,
  getRevisionAuditBlockingIssue,
  buildRevisionCandidateRecord,
  renderCandidatePreview,
  hasMeaningfulDiff,
  syncApplyButton,
  onAuthenticationRequired = () => {},
  applyButtonLabel = '生成优化稿',
  alertFn = globalThis.alert,
} = {}) {
  let inFlight = false;

  function resetApplyButton(button) {
    button.disabled = false;
    button.textContent = applyButtonLabel;
  }

  function getStep03Draft() {
    return {
      title: getState('listingDraftTitle'),
      bullets: getState('listingDraftBullets'),
      description: getState('listingDraftDescription'),
      searchTerms: getState('listingDraftSearchTerms'),
      qa: getState('listingDraftQa'),
    };
  }

  async function applyRevisionSuggestions() {
    if (!getState('isLoggedIn')) {
      onAuthenticationRequired();
      return;
    }

    const suggestions = getState('revisionSuggestions') || [];
    const iterationBase = getPendingCandidate();
    if (!iterationBase) clearPendingCandidate({ clearHistory: true });
    const masterInstructions = collectMasterInstructions();
    setState('masterRevisionInstructions', masterInstructions);
    const supplementalFacts = collectSupplementalFacts();
    setState('revisionSupplementalFacts', supplementalFacts);

    // Old suggestions are advisory only: warn about a changed Step03 draft but
    // keep the user in control of whether to submit the current request.
    const meta = getState('revisionSuggestionsMeta');
    const revisionMode = getRevisionMode();
    const baselineQuality = getRevisionBaselineQuality(meta);
    const storedAdviceText = getState('revisionAdviceText') || '';
    if ((suggestions.length > 0 || normalizeRevisionAdviceText(storedAdviceText)) && meta?.basedOnDraftKey) {
      const currentKey = getCurrentDraftRevisionKey();
      if (currentKey !== meta.basedOnDraftKey) {
        showToast('当前 Listing 已变化，建议重新生成修改建议后再应用。', true);
      }
    }

    if (!canApplyRevision({
      revisionAdviceText: storedAdviceText,
      revisionSuggestionsMeta: meta,
      currentMarketplace: getState('marketplace'),
      diagnosticMarketplace: getState('diagnosticMarketplace'),
      masterInstructions,
      supplementalFacts,
      applyInFlight: inFlight,
      diagnosisInFlight: getState('diagnosisInFlight'),
    })) {
      alertFn?.('当前没有可执行的改稿建议；可以先重新诊断，或直接填写你的补充要求。');
      return;
    }

    const jobId = getState('batchJobId');
    if (!jobId) {
      alertFn?.('请先运行批量分析任务');
      return;
    }

    const button = select('#apply-revision-button');
    if (!button) return;
    button.disabled = true;
    button.textContent = '生成中...';

    const previousSearchTerms = (getState('listingDraftSearchTerms')?.searchTermsEn || '').trim();
    const listingPreview = iterationBase
      ? { ...iterationBase.normalized, ...iterationBase.nextDraft }
      : collectCurrentListing({ full: true, useRevisionSearchTermsDraft: true });
    const listingPreviewSearchTerms = String(
      listingPreview.searchTermsEn
      || listingPreview.searchTerms?.searchTermsEn
      || listingPreview.searchTerms?.searchTerms
      || ''
    ).trim();
    const searchTermsChanged = listingPreviewSearchTerms !== previousSearchTerms;
    const listingText = renderListingRevisionText(listingPreview);
    const listingTextHash = buildListingRevisionTextHash(listingText);
    const revisionAdviceText = buildEffectiveRevisionAdviceText({
      revisionAdviceText: storedAdviceText,
      masterInstructions,
      searchTermsChanged,
    }) || (supplementalFacts.length
      ? 'Use the newly supplied merchant-confirmed facts to resolve the missing-fact issues while preserving all other copy.'
      : '');

    // Suggestion cards remain a compatibility/explanation payload. The prose
    // instruction above is the authoritative execution input.
    const payload = buildRevisionPayload(suggestions);
    const generationLedger = compactRevisionGenerationLedger(
      getState('listingGenerationMeta')?.mergeMeta?.generationLedger || null,
    );
    if (!revisionAdviceText) {
      alertFn?.('当前没有可执行的修改建议，请先重新诊断或填写补充要求。');
      resetApplyButton(button);
      return;
    }

    inFlight = true;
    syncApplyButton();
    const traceId = globalThis.crypto?.randomUUID?.() || `revision-${Date.now()}`;
    let submittedTask = { taskId: '', traceId };
    const clientTrace = [
      {
        functionName: 'client.applyRevisionSuggestions',
        input: {
          jobId,
          schemaVersion: revisionProtocol,
          listingText,
          listingTextHash,
          revisionAdviceText,
          masterInstructions,
          supplementalFacts,
          revisionMode,
          baselineQuality,
          basedOnCandidateVersion: iterationBase?.candidateRecord?.version || 0,
          searchTermsChanged,
        },
      },
      {
        functionName: 'client.buildRevisionPayload:output',
        output: payload,
      },
    ];
    renderExecutionStatus({
      tone: 'running',
      title: '正在提交改稿任务',
      message: '已整理用户采纳建议，正在发送到后端。',
      traceId,
    });

    try {
      const snapshotAsins = resolveSnapshotAsinsForApi(
        getState('batchSnapshots'),
        getState('competitorAsinsIncluded')
      );
      const normalized = await diagnosticFacade.generateRevised(jobId, {
        traceId,
        clientTrace,
        schemaVersion: revisionProtocol,
        listingText,
        listingTextHash,
        revisionAdviceText,
        suggestions: payload,
        masterInstructions,
        supplementalFacts,
        revisionMode,
        baselineQuality,
        searchTermsChanged,
        currentListing: listingPreview,
        generationLedger,
        step04Audit: getState('listingGenerationMeta')?.mergeMeta?.audit || null,
        snapshotAsins,
        keywordUsePolicies: (getState('keywordUsePolicies') || []).filter((item) => item?.manuallyAdjusted === true),
        selectedStKeywords: [...(getState('selectedStKeywords') || new Set())],
        competitorRoles: getState('competitorRoles') || [],
        newProductProjectId: getState('newProductProjectId') || null,
      }, {
        onSubmitted(start) {
          submittedTask = {
            taskId: start.taskId || '',
            traceId: start.traceId || traceId,
          };
          renderExecutionStatus({
            tone: 'running',
            title: '改稿任务已提交',
            message: start.status === 'queued' ? '任务正在排队，系统会持续轮询。' : '任务已经开始执行。',
            ...submittedTask,
          });
        },
        onProgress(status) {
          const queue = status.queue || {};
          const waitHint = queue.waiting > 0 ? `，队列 ${queue.waiting}` : '';
          button.textContent = status.status === 'queued' ? `排队中${waitHint}...` : `修改中${waitHint}...`;
          renderExecutionStatus({
            tone: 'running',
            title: status.status === 'queued' ? '改稿任务排队中' : '正在执行改稿',
            message: status.result?.progress?.message || status.task?.progress?.message || '系统正在处理所选字段。',
            ...submittedTask,
          });
        },
      });

      const functionTrace = appendClientTraceCalls(normalized.meta?.mergeMeta?.functionTrace, [{
        functionName: 'client.diagnosticFacade.generateRevised:output',
        output: {
          task: normalized.task,
          rawErrors: normalized.rawErrors,
          audit: normalized.meta?.mergeMeta?.audit || null,
          changedSections: normalized.meta?.sections || null,
        },
      }]);

      if (normalized.rawErrors?.length) {
        showToast('本轮已保留可用内容，当前 Listing 不会被覆盖。');
      }
      const mergeMeta = normalized.meta?.mergeMeta || {};
      const revisionAudit = mergeMeta.audit;
      const textRevisionDecision = getTextRevisionWritebackDecision(mergeMeta);
      if (textRevisionDecision.isTextRevisionResult && textRevisionDecision.writebackBlocked) {
        const blockedReason = formatRevisionUserFacingReason(textRevisionDecision.reason)
          || '后端没有批准本轮候选稿写回';
        const failedBeforeValidation = /(?:^|_)(?:failed|stale|invalid|error)(?:_|$)/i
          .test(String(textRevisionDecision.status || ''));
        const blockedTitle = failedBeforeValidation
          ? '改稿未完成，当前 Listing 未变化'
          : '候选稿未通过写回校验，当前 Listing 已保留';
        const missingFactQuestions = normalizeRevisionSupplementalFacts(
          textRevisionDecision.revisionReview?.missingFactQuestions
          || mergeMeta.revisionReview?.missingFactQuestions
          || []
        );
        setState('revisionMissingFactQuestions', missingFactQuestions);
        renderFactPanel();
        if (iterationBase) {
          restoreCandidate(iterationBase);
        } else {
          setState('revisionCandidateDraft', null);
        }
        showToast(`${blockedTitle}：${blockedReason}`, true);
        appendClientTraceCalls(functionTrace, [{
          functionName: 'client.applyRevisionSuggestions:textRevisionWritebackDecision',
          output: {
            approved: false,
            status: textRevisionDecision.status,
            reason: textRevisionDecision.reason,
            semanticGate: textRevisionDecision.semanticGate,
            revisionReview: textRevisionDecision.revisionReview,
          },
        }]);
        renderExecutionStatus({
          tone: failedBeforeValidation ? 'error' : 'rejected',
          title: blockedTitle,
          message: `${blockedReason}。原稿未被覆盖；可根据提示修正后重试。`,
          ...submittedTask,
          functionTrace,
        });
        resetApplyButton(button);
        return;
      }

      const reportedChangedFields = Array.isArray(mergeMeta.changedFields) ? mergeMeta.changedFields : [];
      const currentDraft = iterationBase?.nextDraft || getStep03Draft();
      const changedFields = new Set(deriveRevisionChangedFields({ normalized, currentDraft }));
      appendClientTraceCalls(functionTrace, [{
        functionName: 'client.deriveRevisionChangedFields:output',
        output: {
          reportedChangedFields,
          actualChangedFields: [...changedFields],
        },
      }]);
      const invalidChangedFields = getInvalidRevisionChangedFields(normalized, [...changedFields]);
      if (invalidChangedFields.length) {
        throw new Error(`改稿结果缺少已声明的完整字段：${invalidChangedFields.join(', ')}`);
      }
      const stepReady = getState('stepReady') || {};
      const nextDraft = mergeRevisionDraftByChangedFields({
        normalized,
        changedFields: [...changedFields],
        currentDraft,
      });
      const candidateDiffItems = buildRevisionDiffItems(nextDraft);
      const originalStep03Draft = getStep03Draft();
      const writebackChangedFields = deriveRevisionChangedFields({
        normalized: nextDraft,
        currentDraft: originalStep03Draft,
      });
      const isTextRevisionResult = textRevisionDecision.isTextRevisionResult;
      if (!isTextRevisionResult && revisionAudit?.hardReject) {
        const reason = getRevisionAuditBlockingIssue(revisionAudit)?.reason || '候选稿包含不能写回的安全红线';
        const candidateRecord = buildRevisionCandidateRecord({
          nextDraft,
          diffItems: candidateDiffItems,
          audit: revisionAudit,
          task: submittedTask,
        });
        setState('revisionCandidateDraft', candidateRecord);
        showToast(`候选稿命中安全红线，已保留但未覆盖原稿：${reason}`, true);
        appendClientTraceCalls(functionTrace, [{
          functionName: 'client.applyRevisionSuggestions:safetyRedline',
          output: { reason, candidate: candidateRecord },
        }]);
        renderExecutionStatus({
          tone: 'rejected',
          title: '候选稿已保留，原稿未被覆盖',
          message: `命中真正安全红线：${reason}。可在下方查看本次会话的候选稿、差异和完整追踪记录。`,
          ...submittedTask,
          functionTrace,
          candidateRecord,
        });
        resetApplyButton(button);
        return;
      }

      const semanticGate = revisionAudit?.semanticGate;
      if (!isTextRevisionResult && semanticGate?.writebackBlocked) {
        const semanticReview = revisionAudit?.semanticReview || {};
        const semanticIssue = Array.isArray(semanticReview.issues) ? semanticReview.issues[0] : null;
        const semanticReason = formatRevisionUserFacingReason(
          semanticGate.blockReason || semanticIssue?.reason || semanticReview.summary
        ) || '候选稿没有通过写回前语义校验';
        setState('revisionCandidateDraft', null);
        showToast(`候选稿未写回：${semanticReason}`, true);
        appendClientTraceCalls(functionTrace, [{
          functionName: 'client.applyRevisionSuggestions:semanticGate',
          output: { semanticGate, reason: semanticReason },
        }]);
        renderExecutionStatus({
          tone: 'rejected',
          title: '候选稿未通过写回校验，当前 Listing 已保留',
          message: `${semanticReason}。原稿未被覆盖；可根据提示修正后重试。`,
          ...submittedTask,
          functionTrace,
        });
        resetApplyButton(button);
        return;
      }

      const changedFromIterationBase = iterationBase
        ? renderListingRevisionText(nextDraft) !== renderListingRevisionText(currentDraft)
        : hasMeaningfulDiff(nextDraft);
      if (!changedFromIterationBase) {
        showToast(iterationBase ? '新结果与当前候选稿一致，无需新增版本' : '改稿结果与当前内容一致，无需应用');
        appendClientTraceCalls(functionTrace, [{
          functionName: 'client.hasMeaningfulDiff:output',
          output: { changed: false },
        }]);
        renderExecutionStatus({
          tone: 'preview',
          title: '改稿任务完成，无内容变化',
          message: '模型或确定性处理返回的内容与当前 Listing 一致，因此未跳转、未覆盖原稿。',
          ...submittedTask,
          functionTrace,
        });
        resetApplyButton(button);
        return;
      }
      if (!candidateDiffItems.length) {
        showToast('本轮结果已回到 Step03 原稿，无需新增候选版本');
        renderExecutionStatus({
          tone: 'preview',
          title: '本轮结果与 Step03 原稿一致',
          message: '没有需要确认写回的差异；上一候选版本仍保留，可继续优化或放弃。',
          ...submittedTask,
          functionTrace,
        });
        resetApplyButton(button);
        return;
      }

      const candidateVersion = getCandidateVersionCount() + 1;
      const candidateRecord = buildRevisionCandidateRecord({
        nextDraft,
        diffItems: candidateDiffItems,
        audit: revisionAudit,
        task: submittedTask,
        version: candidateVersion,
        basedOnVersion: iterationBase?.candidateRecord?.version || 0,
      });
      pushCandidate({
        normalized,
        nextDraft,
        stepReady,
        changedFields: writebackChangedFields,
        revisionCompletion: textRevisionDecision.isTextRevisionResult && textRevisionDecision.approved === true
          ? { approved: true, status: textRevisionDecision.status }
          : null,
        submittedTask,
        functionTrace,
        candidateRecord,
      });
      setState('revisionMissingFactQuestions', []);
      appendClientTraceCalls(functionTrace, [{
        functionName: 'client.buildRevisionCandidatePreview:output',
        output: { diffItems: candidateDiffItems, writebackPending: true },
      }]);
      showToast(`候选稿 v${candidateVersion} 已生成，请确认 ${candidateDiffItems.length} 项变更`);
      renderExecutionStatus({
        tone: 'preview',
        title: `候选稿 v${candidateVersion} 已生成`,
        message: `请检查下方 ${candidateDiffItems.length} 项修改。可继续提出要求生成下一版，确认后才会覆盖 Step03。`,
        ...submittedTask,
        functionTrace,
        candidateRecord,
      });
      renderCandidatePreview(candidateRecord);
      resetApplyButton(button);
    } catch (error) {
      console.warn('[step6] Revision task did not return a writable candidate; preserving current Listing.', error);
      resetApplyButton(button);
      const errorMessage = error instanceof Error ? error.message : String(error || '');
      const userFacingReason = formatRevisionUserFacingReason(errorMessage)
        || '服务未返回可写回的候选稿';
      const functionTrace = appendClientTraceCalls(
        error?.functionTrace || error?.data?.functionTrace || error?.data?.result?.functionTrace || error?.data?.result?.mergeMeta?.functionTrace || null,
        [{ functionName: 'client.applyRevisionSuggestions:error', error: { message: errorMessage, status: error?.status } }]
      );
      renderExecutionStatus({
        tone: 'error',
        title: '改稿未完成，当前 Listing 未变化',
        message: `${userFacingReason}。原稿未被覆盖，可根据提示修正后重试。`,
        taskId: error?.taskId || submittedTask.taskId,
        traceId: error?.traceId || error?.data?.traceId || submittedTask.traceId,
        functionTrace,
      });
      showToast(`改稿未完成：${userFacingReason}`, true);
    } finally {
      inFlight = false;
      syncApplyButton();
    }
  }

  return {
    applyRevisionSuggestions,
    isInFlight: () => inFlight,
  };
}
